/**
 * The three AI panels that sit on the dashboard, insights and sales pages.
 * They share one shape — heading, AI badge, a generate button, and a body that
 * is either a spinner, the rendered markdown, or a prompt to run it.
 */
import { useState } from 'react';
import { useApp } from '../store/AppContext.jsx';
import { Card, CardHead, CardBody, AIBadge, Spinner } from './Primitives.jsx';
import Markdown from '../lib/markdown.jsx';
import Icon from '../lib/icons.jsx';
import { postSlack } from '../services/automation.js';
import { formatDate } from '../lib/dates.js';

/** Fold control shared by every AI card. */
export function FoldButton({ open, onClick }) {
  return (
    <button
      type="button"
      className="btn sm ghost"
      aria-expanded={open}
      title={open ? 'Hide this' : 'Show this'}
      onClick={onClick}
    >
      {open ? 'Hide' : 'Show'}
      <span className={`fchev${open ? ' up' : ''}`}><Icon name="chev" /></span>
    </button>
  );
}

function AIPanel({ icon, tone, title, state, onRun, runLabel, placeholder, footer }) {
  /* Folded by default: these only hold content once you press Generate, and
     an open card full of placeholder text pushes the real work down. */
  const [open, setOpen] = useState(false);
  return (
    <Card className={open ? '' : 'folded'}>
      <CardHead icon={icon} tone={tone} title={title} badge={<AIBadge />}>
        <button type="button" className="btn sm" onClick={onRun} disabled={state.loading}>
          {state.loading ? <><Spinner />{runLabel.busy}</> : (state.text ? runLabel.again : runLabel.first)}
        </button>
        <FoldButton open={open} onClick={() => setOpen(o => !o)} />
      </CardHead>
      {/* Capped and scrolled, so a long brief cannot push the page down. */}
      <CardBody className={`brief aiscroll${open ? '' : ' hide'}`}>
        {state.loading
          ? <span className="muted">{placeholder.busy}</span>
          : state.text
            ? <><Markdown text={state.text} />{footer}</>
            : <span className="muted">{placeholder.idle}</span>}
      </CardBody>
    </Card>
  );
}

/** Morning read on the client book. */
export function BriefCard() {
  const { brief, generateBrief } = useApp();
  return (
    <AIPanel
      icon="zap"
      tone="g-pink"
      title="Daily brief"
      state={brief}
      onRun={generateBrief}
      runLabel={{ busy: 'Thinking…', first: 'Generate', again: 'Regenerate' }}
      placeholder={{
        busy: 'Reading the ledger…',
        idle: 'Get a prioritised read on today — who to chase, what is at risk, and what to do first.'
      }}
      footer={brief.date ? <div className="muted" style={{ marginTop: 10 }}>Generated {brief.date}</div> : null}
    />
  );
}

/** "What this means" on the insights page. */
export function ExplainCard() {
  const { explain, runExplain } = useApp();
  return (
    <AIPanel
      icon="chart"
      tone="g-purple"
      title="What this means"
      state={explain}
      onRun={runExplain}
      runLabel={{ busy: 'Analysing…', first: 'Analyse', again: 'Refresh' }}
      placeholder={{
        busy: 'Looking for patterns…',
        idle: 'Ask for a read on conversion, revenue leakage and promo performance.'
      }}
    />
  );
}

/** Sales-side brief on the pipeline overview. */
export function SalesBriefCard() {
  const { forecast, generateSalesBrief } = useApp();
  return (
    <AIPanel
      icon="zap"
      tone="g-pink"
      title="Sales brief"
      state={forecast}
      onRun={generateSalesBrief}
      runLabel={{ busy: 'Thinking…', first: 'Generate', again: 'Regenerate' }}
      placeholder={{
        busy: 'Reading the pipeline…',
        idle: 'Get a prioritised read on which deals to push today.'
      }}
    />
  );
}

/**
 * The per-owner digest, with the send actions the original had: mail, copy,
 * and posting to Slack.
 */
export function DigestCard() {
  const { S, digest, generateDigest, alerts, mrr, atRisk, toast } = useApp();
  /* Folded by default: these only hold content once you press Generate, and
     an open card full of placeholder text pushes the real work down. */
  const [open, setOpen] = useState(false);
  const ready = digest.text && digest.date === formatDate(new Date(), false, S.dateFmt);

  const copy = text => {
    if (!navigator.clipboard) { toast('Clipboard unavailable'); return; }
    navigator.clipboard.writeText(text).then(() => toast('Copied'), () => toast('Copy failed'));
  };

  const slackText = () => {
    const m = v => `${S.currency || 'AED'} ${Math.round(v || 0).toLocaleString('en-US')}`;
    return [
      `*${S.workspace || 'Client Master'} — ${digest.date}*`,
      `MRR ${m(mrr)} · ${m(atRisk)} at risk · ${alerts.length} open alerts`,
      ...alerts.slice(0, 8).map(a => `• *${a.client}* — ${a.txt}${a.value ? ` (${m(a.value)}/mo)` : ''}`)
    ].join('\n');
  };

  const toSlack = async () => {
    try {
      await postSlack(S, `*Daily digest — ${digest.date}*\n${digest.text}`);
      toast('Sent to Slack');
    } catch (e) {
      toast(e.message || 'Slack rejected the request — use Copy for Slack instead');
    }
  };

  return (
    <Card className={open ? '' : 'folded'}>
      <CardHead icon="card" tone="g-blue" title="Team digest" badge={<AIBadge />}>
        <button type="button" className="btn sm" onClick={generateDigest} disabled={digest.loading}>
          {digest.loading ? <><Spinner />Writing…</> : (ready ? 'Rewrite' : 'Generate')}
        </button>
        <FoldButton open={open} onClick={() => setOpen(o => !o)} />
      </CardHead>

      <CardBody className={`digest aiscroll${open ? '' : ' hide'}`}>
        {digest.loading ? (
          <span className="muted">Compiling per owner…</span>
        ) : ready ? (
          <>
            <Markdown text={digest.text} />
            <div className="obacts" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn sm primary"
                onClick={() => window.open(
                  `mailto:${encodeURIComponent(S.digestTo || '')}`
                  + `?subject=${encodeURIComponent(`Client digest — ${digest.date}`)}`
                  + `&body=${encodeURIComponent(digest.text)}`, '_blank')}
              >
                <Icon name="card" />Open in mail
              </button>
              <button type="button" className="btn sm" onClick={() => copy(digest.text)}>Copy</button>
              <button type="button" className="btn sm" onClick={toSlack}>Send to Slack</button>
              <button type="button" className="btn sm" onClick={() => copy(slackText())}>Copy for Slack</button>
            </div>
          </>
        ) : (
          <span className="muted">
            A per-owner summary of who needs chasing today, ready to send to the team
            {S.digestOn ? ` — scheduled for ${String(S.digestHour || 9).padStart(2, '0')}:00 daily.` : '.'}
          </span>
        )}
      </CardBody>
    </Card>
  );
}
